import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { KanbanTask } from '../../stores/KanbanTaskStores';
import type { TaskStore } from '../../stores/kanban/TaskStore';

/** What the drawer route needs: the shared `TaskStore` plus the concrete store's `upsertTask`. */
type DrawerStore = TaskStore & { upsertTask: (task: KanbanTask) => void };

export type TaskDrawer = {
  /** Store id of the task the drawer is showing, or null when it is closed. */
  selectedTaskId: string | null;
  /** Path of one task — the two boards address tasks differently. Also what "copy link" copies. */
  taskUrl: (identifier: string | number) => string;
  openTask: (task: KanbanTask) => void;
  close: () => void;
};

/**
 * The URL *is* the drawer's state: a click on a card, a link pasted into a fresh tab and the
 * browser's back button all end up in the effect below. The store is keyed by task id, so a public
 * id from a link is resolved against the loaded tasks first and fetched only when it is unknown.
 */
export function useTaskDrawer(
  store: DrawerStore,
  isAnonymous: boolean,
  tenant: string | undefined,
  /**
   * Whether the board's api client is configured yet. The public board is set up by
   * `AnonymousPage`'s effect (`setConfigApi(token)` + `setMode('public')`), and React runs child
   * effects *before* parent effects — so without this gate a deep link resolves its task while the
   * store still points at `/api/KanbanTask` with no token. That 401 is swallowed into `null` by
   * `getTaskDetails` and surfaces to the user as "task not found" on a board that loaded fine.
   */
  ready: boolean,
): TaskDrawer {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectSlug, taskId } = useParams<{ projectSlug: string; taskId: string }>();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  // Carry the whole query string across open/close. The board's filters live there (useBoardFilters),
  // and on the public board so does the `token`. Rebuilding the URL with only the token — or with
  // nothing — silently dropped the active filter the moment a task was opened, so the board behind
  // the drawer re-read an empty query and showed every task (looked like "the filter stopped working
  // after I changed something"). `location.search` already includes the token on the public board.
  const search = location.search;

  const taskUrl = useCallback(
    (identifier: string | number) =>
      isAnonymous
        ? `/anonymous/project/tasks/${identifier}${search}`
        : `/${tenant}/projects/${projectSlug}/tasks/${identifier}${search}`,
    [isAnonymous, search, tenant, projectSlug],
  );

  const tasksListUrl = useCallback(
    () =>
      isAnonymous
        ? `/anonymous/project/tasks${search}`
        : `/${tenant}/projects/${projectSlug}/tasks${search}`,
    [isAnonymous, search, tenant, projectSlug],
  );

  /** Opening a card only moves the URL; the effect below is what puts a task in the drawer. */
  const openTask = useCallback(
    (task: KanbanTask) => {
      const identifier = task.publicId || task.id;
      if (!identifier) return;
      setSelectedTaskId(String(task.id));
      navigate(taskUrl(identifier), { replace: false });
    },
    [navigate, taskUrl],
  );

  const close = useCallback(() => {
    setSelectedTaskId(null);
    navigate(tasksListUrl(), { replace: true });
  }, [navigate, tasksListUrl]);

  useEffect(() => {
    // Not a no-op we can skip: resolving a task now would use an api client the page has not
    // configured yet. The effect re-runs when `ready` flips, and by then the board's own tasks are
    // usually loaded, so the url resolves from memory instead of a round trip.
    if (!ready) return;

    const identifier = taskId ?? location.pathname.match(/\/tasks\/([^/]+)$/)?.[1];

    if (!identifier) {
      setSelectedTaskId(null);
      return;
    }

    let cancelled = false;

    const openFromUrl = async () => {
      const matches = (task: KanbanTask) => String(task.id) === identifier || task.publicId === identifier;
      const known =
        store.getTask(identifier) ?? store.projectTasks.find(matches) ?? store.deletedProjectTasks.find(matches);

      if (known) {
        // A deleted task lives in its own list; the drawer reads the map, so put it there as well.
        if (!store.getTask(String(known.id))) store.upsertTask(known);
        if (cancelled) return;
        setSelectedTaskId(String(known.id));
        // The board list carries fewer fields than the details endpoint — refresh behind the open
        // drawer rather than making the user wait for it.
        void store.fetchTask(String(known.id)).catch(() => undefined);
        return;
      }

      try {
        const fetched = await store.fetchTask(identifier);
        if (cancelled) return;
        if (!fetched) {
          message.error(t('TaskNotFound'));
          navigate(tasksListUrl(), { replace: true });
          return;
        }
        setSelectedTaskId(String(fetched.id));
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to load task:', error);
        message.error(t('FailedToLoadTask'));
        navigate(tasksListUrl(), { replace: true });
      }
    };

    void openFromUrl();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, location.pathname, store, isAnonymous, ready]);

  return { selectedTaskId, taskUrl, openTask, close };
}
